import React, { useState, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Button, message } from 'antd';
import DateRangeFilter from './DateRangeFilter';
import DynamicFilterSelector from './DynamicFilterSelector';
import { getDynamicFilterOptions } from '../../../../core/api/dashboard';
import { fetchBrandsBySelectedClients } from '../../../../core/api/settings';

const DashboardFilters = ({ 
  setSaleTypeFilters,
  setPaymentModeFilters,
  setDateRangeFilters,
  setBrandFilters,
  setProductFilters,
  setSegmentFilters,
  setCategoryFilters,
  setListFilters,
  userSelectedFilters,
  setUserSelectedFilters,
  cascaderOptions,
  setCascaderOptions,
  onRefreshData,
  onClose = () => { /* Close modal function */ }
}) => {
  const selectedClients = useSelector((state) => state.selectedClients);
  // Store all filter values in tempFilters state
  const [tempFilters, setTempFilters] = useState({
    ...userSelectedFilters,
    dateRange: userSelectedFilters?.dateRange || null,
    brands: userSelectedFilters?.brands || [],
    Product: userSelectedFilters?.Product || [],
    Category: userSelectedFilters?.Category || [],
    Segment: userSelectedFilters?.Segment || [],
    lists: userSelectedFilters?.lists || [],
    saleTypes: userSelectedFilters?.saleTypes || [],
    paymentModes: userSelectedFilters?.paymentModes || []
  });
  const [loadingFilter, setLoadingFilter] = useState(null);
  const debounceTimerRef = useRef(null);
  const [brandOptions, setBrandOptions] = useState([]);
  
  useEffect(() => {
    const fetchBrands = async () => {
      if (selectedClients && selectedClients.length > 0) {
        try {
          setLoadingFilter('brands');
          
          // Pass the entire selectedClients array to the backend
          // Backend will handle 'all', empty array, or specific client IDs
          const response = await getDynamicFilterOptions({ 
            selectedClient: selectedClients, 
            exclude_filter: 'brands' 
          });
          if (response && response.brands) {
            setBrandOptions(response.brands);
            // Update only tempFilters, not userSelectedFilters
            setTempFilters(prev => ({
              ...prev,
              brands: response.brands
            }));
          }
        } catch (error) {
          console.error('Error fetching brands:', error);
        } finally {
          setLoadingFilter(null);
        }
      }
    };
    
    fetchBrands();
  }, [selectedClients]);

  
  const getFilteredBrandOptions = () => {
    return brandOptions;
  };
  

  const fetchDynamicOptions = async (payload, excludeFilter) => {
    try {
      const requestPayload = {
        ...payload,
        exclude_filter: excludeFilter
      };
      
      
      const updated = await getDynamicFilterOptions(requestPayload);
      
      if (updated) {
        handleFilterOptionsUpdate(updated, excludeFilter);
      } else {
        console.warn('Received empty response from dynamic filter options API');
      }
    } catch (err) {
      console.error('Dynamic-filter fetch failed:', err);
      // You could add UI error handling here if needed
    }
  };

  // Handle filter selection (generic handler for all filters)
  const handleFilterSelection = (filterType, values) => {    
    // Special handling for saleTypes to ensure they're properly tracked
    if (filterType === 'saleTypes') {      
      // If no values are selected, select all by default
      if (!values || values.length === 0) {
        values = ['phone', 'mail', 'web', 'unknown'];
      }
    }
    
    // Special handling for brands when they become empty
    if (filterType === 'brands' && values.length === 0 && selectedClients && selectedClients.length > 0) {
      const fetchBrandsForClient = async () => {
        try {
          setLoadingFilter('brands');
          
          // Pass the entire selectedClients array to the backend
          // Backend will handle 'all', empty array, or specific client IDs
          const response = await getDynamicFilterOptions({ 
            selectedClient: selectedClients, 
            exclude_filter: 'brands' 
          });
          
          if (response && response.brands) {
            console.log('Refetching brands since brands array is empty', response.brands);
            setBrandOptions(response.brands);
            // Update only tempFilters, not userSelectedFilters
            setTempFilters(prev => ({
              ...prev,
              brands: response.brands
            }));
          }
        } catch (error) {
          console.error('Error refetching brands:', error);
        } finally {
          setLoadingFilter(null);
        }
      };
      
      fetchBrandsForClient();
      return; // Exit early since we're handling brands separately
    }
    
    // Update only tempFilters, not actual filters
    setTempFilters(prev => ({
      ...prev,
      [filterType]: values,
    }));
    
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    setLoadingFilter(filterType);
    
    debounceTimerRef.current = setTimeout(async () => {
      try {
        // Use tempFilters for API calls to get updated options
        const payload = { 
          ...tempFilters
        };
        
        // Fire request that will come back with trimmed-down options
        await fetchDynamicOptions(payload, filterType);
      } catch (error) {
        console.error('Error fetching dynamic options:', error);
      } finally {
        // Clear loading state
        setLoadingFilter(null);
        debounceTimerRef.current = null;
      }
    }, 800); // 800ms debounce delay - adjust as needed
  };
  const handleFilterOptionsUpdate = (updatedOptions, currentFilterType) => {
        if (!updatedOptions || typeof updatedOptions !== 'object') {
      console.warn('Invalid options received:', updatedOptions);
      return;
    }
    
    if (typeof setCascaderOptions !== 'function') {
      console.warn('setCascaderOptions is not a function. Dynamic filter updates will not be applied.');
      return;
    }
    
    const newCascaderOptions = { ...cascaderOptions };
    let hasUpdates = false;
    const updateOption = (key, sourceKey, targetKey) => {
      if (currentFilterType === targetKey) {
        return false;
      }
      
      if (Array.isArray(updatedOptions[sourceKey])) {
        newCascaderOptions[targetKey] = updatedOptions[sourceKey];
        return true;
      } else if (updatedOptions[sourceKey] === null || updatedOptions[sourceKey] === undefined) {
        // Handle empty arrays - this is important to clear options when no data is available
        newCascaderOptions[targetKey] = [];
        return true;
      }
      return false;
    };
    
    hasUpdates = updateOption('products', 'products', 'Product') || hasUpdates;
    hasUpdates = updateOption('segments', 'segments', 'Segment') || hasUpdates;
    hasUpdates = updateOption('categories', 'categories', 'Category') || hasUpdates;
    hasUpdates = updateOption('lists', 'lists', 'lists') || hasUpdates;
    hasUpdates = updateOption('sale_types', 'sale_types', 'saleTypes') || hasUpdates;
    if (hasUpdates) {
      setCascaderOptions(newCascaderOptions);
    } else {
      console.log('No updates to cascader options');
    }
  };

  // Handle date range change
  const handleDateRangeChange = async (dates) => {
    setLoadingFilter('dateRange');
    
    if (dates && dates.length === 2) {
      try {
        // Update only tempFilters, not actual filters
        setTempFilters(prev => ({
          ...prev,
          dateRange: dates
        }));
        
        await fetchDynamicOptions(
          { ...tempFilters, dateRange: dates },
          'dateRange'
        );
      } catch (error) {
        console.error('Error updating filter options for date range:', error);
      } finally {
        setLoadingFilter(null);
      }
    }
  };



  const handleApplyFilters = () => {
    const isFiltersChanged = JSON.stringify(tempFilters) !== JSON.stringify(userSelectedFilters);
    
    if (isFiltersChanged) {
      // Update all filters from tempFilters
      setUserSelectedFilters(tempFilters);
      
      // Update date range filters
      if (setDateRangeFilters && tempFilters.dateRange) {
        setDateRangeFilters(tempFilters.dateRange);
      }
      
      // Update brand filters
      if (setBrandFilters && tempFilters.brands) {
        setBrandFilters(tempFilters.brands);
      }
      
      // Update product filters
      if (setProductFilters && tempFilters.Product) {
        setProductFilters(tempFilters.Product);
      }
      
      // Update category filters
      if (setCategoryFilters && tempFilters.Category) {
        setCategoryFilters(tempFilters.Category);
      }
      
      // Update segment filters
      if (setSegmentFilters && tempFilters.Segment) {
        setSegmentFilters(tempFilters.Segment);
      }
      
      // Update list filters
      if (setListFilters && tempFilters.lists) {
        setListFilters(tempFilters.lists);
      }
      
      // Update sale type filters
      if (setSaleTypeFilters && tempFilters.saleTypes) {
        const newSaleTypeFilters = {
          phone: tempFilters.saleTypes.includes('phone'),
          mail: tempFilters.saleTypes.includes('mail'),
          web: tempFilters.saleTypes.includes('web'),
          unknown: tempFilters.saleTypes.includes('unknown')
        };
        setSaleTypeFilters(newSaleTypeFilters);
      }
      
      // Update payment mode filters
      if (setPaymentModeFilters && tempFilters.paymentModes) {
        const newPaymentModeFilters = {
          cash: tempFilters.paymentModes.includes('cash'),
          online: tempFilters.paymentModes.includes('online'),
          phone: tempFilters.paymentModes.includes('phone'),
          mail: tempFilters.paymentModes.includes('mail'),
          web: tempFilters.paymentModes.includes('web'),
          unknown: tempFilters.paymentModes.includes('unknown')
        };
        setPaymentModeFilters(newPaymentModeFilters);
      }
      
      // Refresh data if needed
      if (onRefreshData) {
        onRefreshData();
      }
    } else {
      console.log("No filter changes detected, skipping data refresh");
    }
    
    // Close the modal after applying filters (whether filters changed or not)
    onClose();
  };

  const handleClearFilters = () => {
    // Clear only tempFilters
    setTempFilters({
      brands: [],
      Product: [],
      Category: [],
      Segment: [],
      lists: [],
      saleTypes: [],
      paymentModes: [],
      dateRange: null
    });
  };

  return (
    <div className="filter-panel mt-2">
      <div>
        <div className="mb-3">
          <label className="form-label">Date Range</label>
          <DateRangeFilter 
            dateRange={tempFilters.dateRange}
            onChange={handleDateRangeChange}
            loading={loadingFilter === 'dateRange'}
          />
        </div>

        {/* Brand Filter */}
        <DynamicFilterSelector
          label="Brand"
          options={getFilteredBrandOptions()}
          value={tempFilters.brands || []}
          onChange={(values) => handleFilterSelection('brands', values)}
          loading={loadingFilter === 'brands'}
          placeholder="Select brands"
        />

        {/* Product Filter */}
        <DynamicFilterSelector
          label="Product"
          options={cascaderOptions.Product || []}
          value={tempFilters.Product || []}
          onChange={(values) => handleFilterSelection('Product', values)}
          loading={loadingFilter === 'Product'}
        />

        {/* Category Filter */}
        <DynamicFilterSelector
          label="Category"
          options={cascaderOptions.Category || []}
          value={tempFilters.Category || []}
          onChange={(values) => handleFilterSelection('Category', values)}
          loading={loadingFilter === 'Category'}
        />

        {/* Segment Filter */}
        <DynamicFilterSelector
          label="Segment"
          options={cascaderOptions.Segment || []}
          value={tempFilters.Segment || []}
          onChange={(values) => handleFilterSelection('Segment', values)}
          loading={loadingFilter === 'Segment'}
        />

        {/* Sale Type Filter */}
        <DynamicFilterSelector
          label="Sale Type"
          options={cascaderOptions.saleTypes || [
            { value: "phone", label: "Phone" },
            { value: "mail", label: "Mail" },
            { value: "web", label: "Web" },
            { value: "unknown", label: "Unknown" }
          ]}
          value={tempFilters.saleTypes || []}
          onChange={(values) => handleFilterSelection('saleTypes', values)}
          loading={loadingFilter === 'saleTypes'}
        />

        {/* List Filter */}
        <DynamicFilterSelector
          label="List"
          options={cascaderOptions.lists || []}
          value={tempFilters.lists || []}
          onChange={(values) => handleFilterSelection('lists', values)}
          loading={loadingFilter === 'lists'}
        />

        <div className="d-flex gap-2 mt-4">
          <Button 
            type="primary" 
            onClick={handleApplyFilters}
          >
            Apply Filters
          </Button>
          <Button 
            onClick={handleClearFilters}
          >
            Clear Filters
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DashboardFilters;
